import { StrictMode, Fragment } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import "@blocknote/ariakit/style.css";
import App from "./App.tsx";
import "./index.css";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Capacitor } from "@capacitor/core";
import { ThemeProvider } from "next-themes";
import { RevenueCatProvider } from "@/contexts/RevenueCatContext";

import { useDeepLinks } from "@/hooks/use-deep-links";
import { initAppsFlyer } from "@/services/appsflyer";
import { requestTrackingPermission } from "@/services/att";

const queryClient = new QueryClient();

// Initialize ATT + AppsFlyer SDK on native platforms
if (Capacitor.isNativePlatform()) {
  // Request ATT first so AppsFlyer can access the IDFA if authorized
  requestTrackingPermission().then(() => {
    initAppsFlyer();
  });
}

// In React 18 dev mode, StrictMode intentionally double-invokes effects.
// Some Capacitor native plugins (RevenueCat, SpeechRecognition) can break when initialized twice.
const RootWrapper = Capacitor.isNativePlatform() ? Fragment : StrictMode;

// Component to handle deep links
const DeepLinkHandler = () => {
  useDeepLinks();
  return null;
};

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Failed to find the root element");

// Prevent duplicate createRoot calls during HMR
const root = (rootElement as any).__reactRoot ??= createRoot(rootElement);

root.render(
  <RootWrapper>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
        <RevenueCatProvider>
          <BrowserRouter>
            <DeepLinkHandler />
            <App />
            <Toaster />
            <Sonner />
          </BrowserRouter>
        </RevenueCatProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </RootWrapper>,
);

// Log platform information for debugging
console.log("Platform:", Capacitor.getPlatform());
console.log("Is Native Platform:", Capacitor.isNativePlatform());
